import { IonContent, IonPage, IonToast } from '@ionic/react';
import { useAuth, useUser } from '../auth/useAuth';
import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ChanceyCard, FloatingBalls, GameSwitcher, Icon, NumberRow } from '../components/chancey';
import { safeAreaPadding } from '../components/layout/safeArea';
import { TicketDetailSheet } from '../components/tickets/TicketDetailSheet';
import { TicketSnapshotCard } from '../components/tickets/TicketSnapshotCard';
import { formatUsd } from '../data/lotteryData';
import type { ScannedTicket } from '../hooks/scannedTicketUtils';
import { useTicketDetailSheetController } from '../hooks/useTicketDetailSheetController';
import { useGameApi } from '../api/games';
import { useScannedTicketsForGame } from '../hooks/useScannedTickets';
import { useActiveGame } from '../hooks/useActiveGame';
import { useGameInfo } from '../stores/gameInfoStore';
import { useChanceyTheme } from '../theme/ThemeContext';
import { formatNextDrawHeading, formatNextDrawSubheading } from '../utils/nextDrawTime';
import { matchAgainstGameDraw, normalizeDrawDate } from '../utils/ticketMatch';

const HomePage: React.FC = () => {
  const { c, typ, d } = useChanceyTheme();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const history = useHistory();
  const { game, gameId, setGameId } = useActiveGame();
  const { getGameInfo, getPublicGameInfo, getGameDraw } = useGameApi();
  const { loading, error, data, load, clearError } = useGameInfo(gameId);
  const {
    tickets,
    gameTickets: activeGameTickets,
    displayTickets,
    addTicket,
    updateTicket,
    removeTicket,
  } = useScannedTicketsForGame(gameId);
  const refreshAttemptedRef = useRef<Set<string>>(new Set());
  const latest = data?.draws?.[0];
  const recentDraws = data?.draws.slice(0, 5) ?? [];
  const ticketPreview = displayTickets.slice(0, 5);
  const hasMoreDraws = (data?.draws.length ?? 0) > 5;
  const hasMoreTickets = displayTickets.length > 5;
  const nextJackpot = typeof data?.nextDraw?.jackpot === 'number' ? data.nextDraw.jackpot : null;
  const nextDraw = data?.nextDraw?.iso ? parseIso(data.nextDraw.iso) : null;
  const ticketSheet = useTicketDetailSheetController({
    tickets,
    gameId,
    getGameDraw,
    addTicket,
    updateTicket,
    removeTicket,
  });
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  const todayLabel = React.useMemo(() => formatTodayLabel(new Date(nowTick)), [nowTick]);
  const countdown = nextDraw ? countdownTo(nextDraw, nowTick) : null;
  const getPublicGameInfoRef = React.useRef(getPublicGameInfo);
  getPublicGameInfoRef.current = getPublicGameInfo;
  const requireAccount = React.useCallback(
    (returnTo: string) => {
      if (isSignedIn) {
        history.push(returnTo);
        return;
      }
      history.push('/sign-in', { returnTo });
    },
    [history, isSignedIn]
  );

  React.useEffect(() => {
    if (!authLoaded || isSignedIn) return;
    void load({ getGameInfo: getPublicGameInfoRef.current }, 5);
  }, [authLoaded, isSignedIn, load]);

  // Keep the countdown accurate by updating on minute boundaries.
  // Pull `getTime()` into a local so the dep array stays statically
  // analyzable (the linter rejects `nextDraw?.getTime()` directly because
  // it cannot prove the call is pure across renders).
  const nextDrawMs = nextDraw ? nextDraw.getTime() : null;
  React.useEffect(() => {
    if (nextDrawMs === null) return;
    let interval: number | null = null;
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    const timeout = window.setTimeout(() => {
      setNowTick(Date.now());
      interval = window.setInterval(() => setNowTick(Date.now()), 60_000);
    }, msUntilNextMinute);
    return () => {
      window.clearTimeout(timeout);
      if (interval !== null) window.clearInterval(interval);
    };
  }, [nextDrawMs]);

  const todayYmd = React.useMemo(() => formatLocalYmd(new Date(nowTick)), [nowTick]);

  React.useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    const candidates = activeGameTickets.filter((ticket) =>
      shouldRefreshTicketResult(ticket, todayYmd)
    );
    if (candidates.length === 0) return;

    void (async () => {
      for (const ticket of candidates) {
        const drawDate = normalizeDrawDate(ticket.drawDate);
        if (!drawDate) continue;
        const ticketGameId = ticket.gameId ?? gameId;
        const attemptKey = `${ticket.id}:${ticket.status}:${ticketGameId}:${drawDate}`;
        if (refreshAttemptedRef.current.has(attemptKey)) continue;
        refreshAttemptedRef.current.add(attemptKey);

        try {
          const gameDraw = await getGameDraw(ticketGameId, drawDate);
          if (cancelled) return;
          const match = matchAgainstGameDraw(
            { main: ticket.main, bonus: ticket.bonus, drawDate },
            gameDraw
          );
          const nextPrize = match.prize > 0 ? match.prize : undefined;
          const nextMatch = winningMatchFromResult(match);
          if (
            match.outcome !== ticket.status ||
            nextPrize !== ticket.prize ||
            drawDate !== ticket.drawDate ||
            !sameTicketMatch(ticket.match, nextMatch)
          ) {
            await updateTicket(ticket.id, {
              status: match.outcome,
              prize: nextPrize,
              drawDate,
              match: nextMatch,
            });
          }
        } catch {
          /* best effort: stale tickets will be retried on a later visit */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeGameTickets, gameId, getGameDraw, isSignedIn, todayYmd, updateTicket]);

  const greetingName =
    user?.firstName?.trim() ||
    user?.fullName?.trim().split(/\s+/)[0] ||
    user?.username?.trim() ||
    'there';

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        <div
          className="flex flex-col gap-1 text-[color:var(--chancey-ink)]"
          style={{
            ...safeAreaPadding(8, d.pad, 16, d.pad),
            gap: d.gap + 4,
            color: c.ink,
            fontFamily: typ.body,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-0.5 pb-1.5 pt-1">
            <div>
              <div
                className="text-xs font-medium uppercase tracking-[0.08em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                {todayLabel}
              </div>
              <div
                className="mt-0.5 text-[32px] leading-tight"
                style={{
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  letterSpacing: typ.displayLetter,
                  color: c.ink,
                }}
              >
                {isSignedIn ? `Hey, ${greetingName}` : 'Welcome to Chancey'}
              </div>
            </div>
            {!isSignedIn && (
              <button
                type="button"
                onClick={() => requireAccount('/home')}
                className="shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold"
                style={{ background: c.ink, color: c.bg, fontFamily: typ.body }}
              >
                Sign in
              </button>
            )}
          </div>

          {/* Jackpot hero */}
          <div
            className="relative min-h-[220px] overflow-hidden rounded-[28px] p-[22px] text-white"
            style={{ background: c.gradient }}
          >
            <div
              className="absolute left-[-12px] top-1/2 h-6 w-6 -translate-y-1/2 rounded-full"
              style={{ background: c.bg }}
            />
            <div
              className="absolute right-[-12px] top-1/2 h-6 w-6 -translate-y-1/2 rounded-full"
              style={{ background: c.bg }}
            />
            <GameSwitcher game={game} onSelectGame={setGameId} />
            <div
              className="mt-3.5 text-[11px] font-semibold uppercase tracking-[0.12em] opacity-80"
              style={{ fontFamily: typ.body }}
            >
              Next draw · {nextDraw ? formatNextDrawHeading(nextDraw) : game.drawDays}
            </div>
            <div className="mt-2.5 flex items-baseline gap-1">
              <span
                className="text-[22px] opacity-90"
                style={{ fontFamily: typ.display, fontWeight: typ.displayWeight }}
              >
                $
              </span>
              <span
                className="text-[68px] leading-[0.9]"
                style={{ fontFamily: typ.display, fontWeight: typ.displayWeight }}
              >
                {typeof nextJackpot === 'number'
                  ? Math.round(nextJackpot / 1_000_000)
                  : latest
                    ? Math.round(latest.jackpot / 1_000_000)
                    : loading
                      ? '…'
                      : '—'}
              </span>
              <span
                className="ml-0.5 text-2xl opacity-90"
                style={{ fontFamily: typ.display, fontWeight: typ.displayWeight }}
              >
                million
              </span>
            </div>
            <div className="mt-1.5 text-[13px] opacity-85" style={{ fontFamily: typ.body }}>
              {nextDraw
                ? formatNextDrawSubheading(nextDraw, { now: new Date(nowTick) })
                : data
                  ? (data.nextDraw?.label ?? '—')
                  : loading
                    ? 'Loading draw time…'
                    : '—'}
            </div>
            <div className="mt-[18px] flex gap-2">
              {[
                { v: countdown?.days, l: 'days' },
                { v: countdown?.hours, l: 'hrs' },
                { v: countdown?.mins, l: 'min' },
              ].map((t, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-xl py-2 text-center backdrop-blur-md"
                  style={{ background: 'rgba(255,255,255,0.18)' }}
                >
                  <div
                    className="font-mono text-[22px] font-semibold tracking-tight"
                    style={{ fontFamily: typ.mono }}
                  >
                    {typeof t.v === 'number' ? String(t.v).padStart(2, '0') : '—'}
                  </div>
                  <div
                    className="text-[10px] uppercase tracking-[0.1em] opacity-80"
                    style={{ fontFamily: typ.body }}
                  >
                    {t.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <ChanceyCard c={c} pad={16}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                    Couldn’t load game history
                  </div>
                  <div className="mt-0.5 text-xs" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                    Check your connection and try again.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    clearError();
                    void load(
                      { getGameInfo: isSignedIn ? getGameInfo : getPublicGameInfo },
                      isSignedIn ? 20 : 5
                    );
                  }}
                  className="shrink-0 rounded-xl px-3 py-2 text-xs font-semibold"
                  style={{ background: c.ink, color: c.bg, fontFamily: typ.body }}
                >
                  Retry
                </button>
              </div>
            </ChanceyCard>
          )}

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-3" style={{ gap: d.gap }}>
            <ChanceyCard c={c} pad={14} onClick={() => requireAccount('/scan')} className="cursor-pointer">
              <div className="mb-3 flex items-start justify-between">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: c.ink, color: c.bg }}
                >
                  {Icon.cam(22, c.bg)}
                </div>
                {Icon.arrow(16, c.inkMuted)}
              </div>
              <div className="text-[15px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                {isSignedIn ? 'Scan ticket' : 'Sign in to scan'}
              </div>
              <div className="mt-0.5 text-xs" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                {isSignedIn ? 'Check if you won' : 'Save and check tickets'}
              </div>
            </ChanceyCard>
            <ChanceyCard c={c} pad={14} onClick={() => requireAccount('/rules')} className="cursor-pointer">
              <div className="mb-3 flex items-start justify-between">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: c.accent, color: c.accentInk }}
                >
                  {Icon.wand(22, c.accentInk)}
                </div>
                {Icon.arrow(16, c.inkMuted)}
              </div>
              <div className="text-[15px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                {isSignedIn ? 'Build a pick' : 'Sign in to build'}
              </div>
              <div className="mt-0.5 text-xs" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                Stats-driven numbers
              </div>
            </ChanceyCard>
          </div>

          {/* Recent draws */}
          <div className="flex items-baseline justify-between px-0.5 pt-1.5">
            <div
              className="text-xl"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              Recent draws
            </div>
            {isSignedIn && hasMoreDraws ? (
              <button
                type="button"
                onClick={() => history.push('/home/draws')}
                aria-label="View all recent draws"
                className="text-xs font-semibold"
                style={{ color: c.accent, fontFamily: typ.body }}
              >
                More
              </button>
            ) : (
              <div className="text-xs" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                {latest ? formatIsoDate(latest.iso) : loading ? 'Loading…' : '—'}
              </div>
            )}
          </div>
          {recentDraws.length > 0 ? (
            <div
              data-testid="home-recent-draws-rail"
              className="hide-scrollbar flex gap-2.5 overflow-x-auto pb-1"
              style={{
                marginLeft: -d.pad,
                marginRight: -d.pad,
                paddingLeft: d.pad,
                paddingRight: d.pad,
              }}
            >
              {recentDraws.map((draw) => (
                <div
                  key={draw.iso}
                  className="min-w-[240px] shrink-0 rounded-[18px] border p-3.5"
                  style={{ background: c.surface, borderColor: c.line }}
                >
                  <div className="mb-2.5 flex items-center justify-between gap-3">
                    <div
                      className="font-mono text-[12px] font-semibold"
                      style={{ color: c.ink, fontFamily: typ.mono }}
                    >
                      {formatIsoDate(draw.iso)}
                    </div>
                    <div
                      className="text-[11px]"
                      style={{ color: c.inkMuted, fontFamily: typ.body }}
                    >
                      {formatUsd(draw.jackpot)}
                    </div>
                  </div>
                  <NumberRow main={draw.main} bonus={draw.bonus} size={30} gap={5} c={c} />
                </div>
              ))}
            </div>
          ) : (
            <ChanceyCard c={c} pad={18}>
              <div className="flex gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-10 w-10 rounded-full"
                    style={{ background: c.surfaceAlt, opacity: loading ? 0.7 : 0.35 }}
                  />
                ))}
              </div>
            </ChanceyCard>
          )}

          {!isSignedIn && (
            <>
              <div className="px-0.5 pt-1.5">
                <div
                  className="text-xl"
                  style={{
                    fontFamily: typ.display,
                    fontWeight: typ.displayWeight,
                    letterSpacing: typ.displayLetter,
                  }}
                >
                  Save your tickets
                </div>
              </div>
              <button
                type="button"
                onClick={() => requireAccount('/scan')}
                className="relative flex min-h-[168px] items-center gap-3 overflow-hidden rounded-[24px] border p-5 text-left"
                style={{ borderColor: c.line, background: c.surface }}
              >
                <div className="relative z-[1] max-w-[250px]">
                  <div
                    className="text-[22px] leading-tight"
                    style={{
                      fontFamily: typ.display,
                      fontWeight: typ.displayWeight,
                      letterSpacing: typ.displayLetter,
                      color: c.ink,
                    }}
                  >
                    Sign in to scan and save tickets
                  </div>
                  <div
                    className="mt-2 text-[13px] leading-snug"
                    style={{ color: c.inkSoft, fontFamily: typ.body }}
                  >
                    Keep tickets, results, picks, and reminders synced across devices.
                  </div>
                  <div
                    className="mt-3.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold"
                    style={{ background: c.ink, color: c.bg, fontFamily: typ.body }}
                  >
                    Sign in {Icon.arrow(12, c.bg)}
                  </div>
                </div>
                <div className="pointer-events-none absolute right-[-12px] top-1/2 -translate-y-1/2">
                  <FloatingBalls c={c} typ={typ} size="sm" />
                </div>
              </button>
            </>
          )}

          {isSignedIn && (
            <>
          {/* Your tickets */}
          <div className="flex items-baseline justify-between px-0.5 pt-1.5">
            <div
              className="text-xl"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
              }}
            >
              Your tickets
            </div>
            {displayTickets.length > 0 && (
              hasMoreTickets ? (
                <button
                  type="button"
                  onClick={() => history.push('/home/tickets')}
                  aria-label="View all saved tickets"
                  className="text-xs font-semibold"
                  style={{ color: c.accent, fontFamily: typ.body }}
                >
                  More
                </button>
              ) : (
                <div className="text-xs font-medium" style={{ color: c.accent, fontFamily: typ.body }}>
                  {displayTickets.length} saved
                </div>
              )
            )}
          </div>

          {displayTickets.length === 0 ? (
            <button
              type="button"
              onClick={() => history.push('/scan')}
              aria-label="Scan your first ticket. Opens the camera."
              className="relative flex items-stretch gap-3 overflow-hidden rounded-[24px] border p-5 text-left"
              style={{
                borderColor: c.line,
                background: c.surface,
                minHeight: 180,
              }}
            >
              <div className="relative z-[1] flex flex-1 flex-col justify-center">
                <div
                  className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em]"
                  style={{ color: c.accent, fontFamily: typ.body }}
                >
                  Start here
                </div>
                <div
                  className="text-[22px] leading-tight"
                  style={{
                    fontFamily: typ.display,
                    fontWeight: typ.displayWeight,
                    letterSpacing: typ.displayLetter,
                    color: c.ink,
                  }}
                >
                  Scan your first {game.shortName} ticket
                </div>
                <div
                  className="mt-2 max-w-[220px] text-[13px] leading-snug"
                  style={{ color: c.inkSoft, fontFamily: typ.body }}
                >
                  Line up the numbers on your ticket in the camera. We identify the game, save the ticket, and show it here when it matches {game.shortName}.
                </div>
                <div
                  className="mt-3.5 inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-[12px] font-semibold"
                  style={{ background: c.ink, color: c.bg, fontFamily: typ.body }}
                >
                  Open scanner {Icon.arrow(12, c.bg)}
                </div>
              </div>
              <div className="pointer-events-none absolute right-[-12px] top-1/2 -translate-y-1/2">
                <FloatingBalls c={c} typ={typ} size="sm" />
              </div>
            </button>
          ) : (
            <>
              <div
                data-testid="home-tickets-rail"
                className="hide-scrollbar flex gap-2.5 overflow-x-auto pb-1"
                style={{
                  marginLeft: -d.pad,
                  marginRight: -d.pad,
                  paddingLeft: d.pad,
                  paddingRight: d.pad,
                }}
              >
                {ticketPreview.map((t) => (
                  <TicketSnapshotCard
                    key={t.id}
                    ticket={t}
                    c={c}
                    typ={typ}
                    onOpen={ticketSheet.openTicket}
                  />
                ))}
              </div>
            </>
          )}
            </>
          )}
        </div>
        <TicketDetailSheet
          ticket={ticketSheet.selectedTicket}
          onClose={ticketSheet.closeSheet}
          onDelete={ticketSheet.handleDelete}
          onRefreshResult={ticketSheet.handleRefreshTicket}
          refreshing={ticketSheet.refreshing}
          refreshError={ticketSheet.refreshError}
          c={c}
          typ={typ}
          triggerRef={ticketSheet.triggerRef}
        />
        <IonToast
          isOpen={ticketSheet.toastOpen}
          message="We removed that ticket."
          duration={5000}
          onDidDismiss={() => {
            ticketSheet.setToastOpen(false);
            ticketSheet.clearUndoTicket();
          }}
          buttons={[
            {
              text: 'Undo',
              role: 'cancel',
              handler: () => {
                void ticketSheet.handleUndo();
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default HomePage;

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function parseIso(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shouldRefreshTicketResult(ticket: ScannedTicket, todayYmd: string): boolean {
  if (ticket.status !== 'pending' && ticket.status !== 'unknown-draw') return false;
  const drawDate = normalizeDrawDate(ticket.drawDate);
  return Boolean(drawDate && drawDate < todayYmd);
}

function winningMatchFromResult(match: ReturnType<typeof matchAgainstGameDraw>): ScannedTicket['match'] | undefined {
  if (match.outcome !== 'jackpot' && match.outcome !== 'won-small') return undefined;
  if (match.matchedMain.length === 0 && match.matchedBonus.length === 0) return undefined;
  return { main: match.matchedMain, bonus: match.matchedBonus };
}

function sameTicketMatch(a: ScannedTicket['match'], b: ScannedTicket['match']): boolean {
  return sameNumbers(a?.main ?? [], b?.main ?? []) && sameNumbers(a?.bonus ?? [], b?.bonus ?? []);
}

function sameNumbers(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const aa = [...a].sort((x, y) => x - y);
  const bb = [...b].sort((x, y) => x - y);
  return aa.every((n, i) => n === bb[i]);
}

function countdownTo(target: Date, nowMs: number): { days: number; hours: number; mins: number } {
  const ms = Math.max(0, target.getTime() - nowMs);
  const totalMins = Math.floor(ms / (60 * 1000));
  const days = Math.floor(totalMins / (60 * 24));
  const hours = Math.floor((totalMins - days * 60 * 24) / 60);
  const mins = totalMins - days * 60 * 24 - hours * 60;
  return { days, hours, mins };
}

function formatTodayLabel(d: Date): string {
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const monthDay = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${weekday} · ${monthDay}`;
}
